import React, { useEffect, useRef, useState } from 'react';
import { HexAlphaColorPicker } from 'react-colorful';
import Box from '@mui/material/Box';
import FormLabel from '@mui/material/FormLabel';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import DeleteOutlined from '@mui/icons-material/DeleteOutlined';
import { Button } from '@/ui';

interface ColorPickerProps {
  fieldKey: string;
  color: string;
  setColor: (color: string) => void;
  label?: string;
  labelComponent?: React.ReactNode;
  disabled?: boolean;
}

const SWATCH_SX = {
  width: '3em',
  height: '3em',
  borderRadius: 1,
  border: 1,
  position: 'relative',
  boxShadow: 2,
} as const;

/** The "no colour" marker: a red slash across an empty swatch. */
const SLASH_SX = {
  position: 'absolute',
  top: '50%',
  left: '50%',
  width: '100%',
  height: '2px',
  backgroundColor: 'error.main',
  transform: 'translate(-50%, -50%) rotate(-45deg)',
  transformOrigin: 'center',
} as const;

const ColorPicker: React.FC<ColorPickerProps> = ({
  fieldKey,
  color,
  setColor,
  label,
  labelComponent,
  disabled = false,
}) => {
  const [isPickerVisible, setPickerVisible] = useState(false);
  const [localInputValue, setLocalInputValue] = useState<string>(color);
  // reactstrap's Popover found its anchor by element id; MUI's takes the element itself.
  const swatchRef = useRef<HTMLDivElement>(null);

  const togglePicker = () => setPickerVisible(disabled ? false : !isPickerVisible);

  // Check if color is empty or null
  const isColorEmpty = !color || color === '';

  // Sync local input value with prop changes
  useEffect(() => {
    setLocalInputValue(color === '' ? 'none' : color);
  }, [color]);

  // Function to check if a color is a valid hex format
  const isValidHexColor = (colorValue: string): boolean => {
    if (!colorValue) return false;
    const hexRegex = /^#([0-9A-Fa-f]{3}){1,2}([0-9A-Fa-f]{2})?$/;
    return hexRegex.test(colorValue);
  };

  // Get the color value to use for the HexAlphaColorPicker. Default a fresh
  // pick to full opacity, not transparent - otherwise picking a hue without
  const getPickerColor = (): string => {
    return isValidHexColor(color) ? color : '#000000ff';
  };

  // Handle color picker change
  const handlePickerChange = (newColor: string) => {
    if ((isValidHexColor(newColor) && localInputValue !== 'none') || newColor !== '#00000000') {
      setColor(newColor);
    }
  };

  // Handle text input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setLocalInputValue(newValue);
    if (newValue === 'none') setColor('');
    else setColor(newValue);
  };

  // Handle remove button click
  const handleRemoveClick = () => {
    setColor('');
    setLocalInputValue('none');
  };

  return (
    <Stack direction='row' spacing={2} sx={{ alignItems: 'center', width: '100%' }}>
      {(labelComponent || label) && (labelComponent ?? <FormLabel>{label}</FormLabel>)}
      <Stack direction='row' spacing={1} sx={{ alignItems: 'center' }}>
        <Box
          ref={swatchRef}
          id={'colorPicker-' + fieldKey}
          role='button'
          tabIndex={disabled ? -1 : 0}
          aria-label={label ? `${label} swatch` : 'Colour swatch'}
          aria-haspopup='dialog'
          aria-expanded={isPickerVisible}
          aria-disabled={disabled}
          onClick={togglePicker}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              togglePicker();
            }
          }}
          sx={{
            ...SWATCH_SX,
            cursor: disabled ? 'default' : 'pointer',
            backgroundColor: isColorEmpty ? 'common.white' : color,
            borderColor: isColorEmpty ? 'error.main' : 'common.black',
          }}
        >
          {isColorEmpty && <Box sx={SLASH_SX} />}
        </Box>
        {color && (
          <Typography variant='body2' sx={{ color: 'text.secondary', fontFamily: 'monospace' }}>
            {color}
          </Typography>
        )}
      </Stack>
      <Popover
        open={isPickerVisible}
        anchorEl={swatchRef.current}
        onClose={togglePicker}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Stack spacing={2} sx={{ p: 2 }}>
          <HexAlphaColorPicker color={getPickerColor()} onChange={handlePickerChange} />
          <TextField
            value={localInputValue}
            onChange={handleInputChange}
            placeholder='Enter color (hex, rgb, css variable, etc.)'
          />
          <Button color='danger' onClick={handleRemoveClick} startIcon={<DeleteOutlined />} block>
            Clear Value
          </Button>
        </Stack>
      </Popover>
    </Stack>
  );
};

export default ColorPicker;
